import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Shield, Users, ShoppingBag, Shirt, CreditCard, Settings, Plus } from 'lucide-react';

const PermissionsPage = () => {
  // Placeholder roles
  const roles = [
    { id: 1, name: 'Admin', description: 'Full access to all features', users: 1, color: 'bg-amber-500/20 text-amber-600' },
    { id: 2, name: 'Manager', description: 'Manage orders and customers', users: 0, color: 'bg-blue-500/20 text-blue-600' },
    { id: 3, name: 'Staff', description: 'View and update orders', users: 0, color: 'bg-green-500/20 text-green-600' },
    { id: 4, name: 'Tailor', description: 'Access to production features', users: 0, color: 'bg-purple-500/20 text-purple-600' },
  ];

  // Placeholder permissions
  const permissionGroups = [
    {
      name: 'Orders',
      icon: ShoppingBag,
      permissions: ['View Orders', 'Create Orders', 'Edit Orders', 'Delete Orders'],
    },
    {
      name: 'Customers',
      icon: Users,
      permissions: ['View Customers', 'Create Customers', 'Edit Customers', 'Delete Customers'],
    },
    {
      name: 'Production',
      icon: Shirt,
      permissions: ['View Garments', 'Update Status', 'Manage Fabric Orders'],
    },
    {
      name: 'Finance',
      icon: CreditCard,
      permissions: ['View Payments', 'Process Payments', 'Issue Refunds'],
    },
  ];

  return (
    <div className="space-y-6 p-3">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Permissions
          </h1>
          <p className="text-muted-foreground">Manage roles and access permissions</p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Create Role
        </Button>
      </div>

      {/* Roles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {roles.map((role) => (
          <Card key={role.id} className="border-white/10 bg-card hover:bg-secondary/30 cursor-pointer transition-colors">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-3">
                <Badge className={role.color}>{role.name}</Badge>
                <span className="text-xs text-muted-foreground">{role.users} users</span>
              </div>
              <p className="text-sm text-muted-foreground">{role.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Permission Groups */}
      <Card className="border-white/10 bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            Permission Settings
          </CardTitle>
          <CardDescription>Configure what each role can access</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {permissionGroups.map((group) => (
            <div key={group.name} className="space-y-3">
              <div className="flex items-center gap-2">
                <group.icon className="w-4 h-4 text-muted-foreground" />
                <h4 className="font-medium">{group.name}</h4>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pl-6">
                {group.permissions.map((permission) => (
                  <div key={permission} className="flex items-center space-x-2">
                    <Switch id={permission} disabled />
                    <Label htmlFor={permission} className="text-sm text-muted-foreground">
                      {permission}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-dashed border-2 border-slate-300 dark:border-slate-700 bg-transparent">
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <p className="text-sm">🚧 Permissions functionality coming soon</p>
            <p className="text-xs mt-1">This page will allow you to create custom roles and manage detailed access permissions</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PermissionsPage;
